// Phase 0085-B — Assessment engine API client.
import apiClient from '@/shared/api/client'

export interface AssessmentTemplate {
  id: string
  template_code: string
  template_name: string
  description: string | null
  version_no: number
  is_default: boolean
  active: boolean
  approval_status: string
}

export interface AssessmentSection {
  id: string
  template_id: string
  section_code: string
  section_name: string
  section_order: number
  applies_to_role: string
  description: string | null
  active: boolean
  questions?: AssessmentQuestion[]
}

export interface AssessmentQuestion {
  id: string
  section_id: string
  question_code: string
  question_text: string
  question_type: string
  weight: number
  auto_kpi_source: string | null
  answerable_by_role: string
  sequence_no: number
  is_mandatory: boolean
  min_value: number | null
  max_value: number | null
  contributes_to_dimension: string | null
  active: boolean
}

export interface AssessmentCycle {
  id: string
  cycle_code: string
  cycle_name: string
  template_id: string
  period_start: string
  period_end: string
  due_self_by: string | null
  due_reporting_by: string | null
  due_reviewing_by: string | null
  status: string
  launched_at: string | null
  active: boolean
}

export type AssessmentStage =
  | 'DRAFT' | 'SELF_IN_PROGRESS' | 'SELF_SUBMITTED'
  | 'REPORTING_IN_PROGRESS' | 'REPORTING_SUBMITTED'
  | 'REVIEWING_IN_PROGRESS' | 'REVIEWING_SUBMITTED'
  | 'REPRESENTATION' | 'HR_REVIEW' | 'LOCKED' | 'REOPENED'

export interface EmployeeAssessment {
  id: string
  cycle_id: string
  template_id: string
  employee_id: string
  employee_user_id: string | null
  reporting_authority_user_id: string | null
  reviewing_authority_user_id: string | null
  current_stage: AssessmentStage
  self_submitted_at: string | null
  reporting_submitted_at: string | null
  reviewing_submitted_at: string | null
  hr_locked_at: string | null
  total_raw_score: number | null
  total_max_score: number | null
  normalized_score_100: number | null
  performance_grade: string | null
  active: boolean
  remarks: string | null
}

export interface AssessmentAnswer {
  id: string
  employee_assessment_id: string
  question_id: string
  answered_by_role: string
  answered_by_user_id: string | null
  answer_value_numeric: number | null
  answer_value_text: string | null
  answer_value_option_id: string | null
  answer_value_bool: boolean | null
  remarks: string | null
  auto_fetched_flag: boolean
  auto_fetched_source: string | null
  version_no: number
  answered_at: string
}

export interface AssessmentScore {
  id: string
  employee_assessment_id: string
  dimension: string
  raw_score: number
  max_score: number
  normalized_pct: number
  weight_pct: number
  weighted_score: number
  computed_at: string
}

export interface AssessmentRepresentation {
  id: string
  employee_assessment_id: string
  submitted_by_user_id: string | null
  representation_text: string
  submitted_at: string
  hr_response_text: string | null
  hr_responded_by: string | null
  hr_responded_at: string | null
  status: string
  active: boolean
}

export interface AssessmentBundle {
  assessment: EmployeeAssessment
  cycle: AssessmentCycle | null
  template: AssessmentTemplate & { sections: AssessmentSection[] }
  answers: AssessmentAnswer[]
  scores: AssessmentScore[]
  representation: AssessmentRepresentation[]
  workflow: Array<Record<string, unknown>>
}

const P = '/hr/assessments'

export const assessmentApi = {
  // Templates
  listTemplates: async (): Promise<AssessmentTemplate[]> =>
    (await apiClient.get(`${P}/templates`)).data,
  createTemplate: async (body: Partial<AssessmentTemplate>): Promise<AssessmentTemplate> =>
    (await apiClient.post(`${P}/templates`, body)).data,
  getTemplate: async (id: string): Promise<AssessmentTemplate & { sections: AssessmentSection[] }> =>
    (await apiClient.get(`${P}/templates/${id}`)).data,
  createSection: async (templateId: string, body: Partial<AssessmentSection>): Promise<AssessmentSection> =>
    (await apiClient.post(`${P}/templates/${templateId}/sections`, body)).data,
  createQuestion: async (sectionId: string, body: Partial<AssessmentQuestion>): Promise<AssessmentQuestion> =>
    (await apiClient.post(`${P}/sections/${sectionId}/questions`, body)).data,

  // Cycles
  listCycles: async (): Promise<AssessmentCycle[]> =>
    (await apiClient.get(`${P}/cycles`)).data,
  createCycle: async (body: Partial<AssessmentCycle>): Promise<AssessmentCycle> =>
    (await apiClient.post(`${P}/cycles`, body)).data,
  launchCycle: async (cycleId: string, employee_ids?: string[]): Promise<Record<string, unknown>> =>
    (await apiClient.post(`${P}/cycles/${cycleId}/launch`, { employee_ids })).data,

  // Employee assessments
  mine: async (): Promise<EmployeeAssessment[]> =>
    (await apiClient.get(`${P}/mine`)).data,
  get: async (eaId: string): Promise<AssessmentBundle> =>
    (await apiClient.get(`${P}/${eaId}`)).data,
  assignReviewers: async (eaId: string, body: { reporting_user_id?: string; reviewing_user_id?: string }) =>
    (await apiClient.post(`${P}/${eaId}/assign-reviewers`, body)).data,

  upsertAnswer: async (eaId: string, body: {
    question_id: string
    answered_by_role: string
    answer_value_numeric?: number | null
    answer_value_text?: string | null
    answer_value_option_id?: string | null
    answer_value_bool?: boolean | null
    remarks?: string | null
  }): Promise<AssessmentAnswer> =>
    (await apiClient.post(`${P}/${eaId}/answers`, body)).data,

  submitSelf: async (eaId: string): Promise<EmployeeAssessment> =>
    (await apiClient.post(`${P}/${eaId}/submit-self`)).data,
  submitReporting: async (eaId: string): Promise<EmployeeAssessment> =>
    (await apiClient.post(`${P}/${eaId}/submit-reporting`)).data,
  submitReviewing: async (eaId: string, remarks?: string): Promise<EmployeeAssessment> =>
    (await apiClient.post(`${P}/${eaId}/submit-reviewing`, { remarks })).data,
  submitRepresentation: async (eaId: string, text: string): Promise<EmployeeAssessment> =>
    (await apiClient.post(`${P}/${eaId}/representation`, { text })).data,
  hrRespond: async (eaId: string, body: { status: string; response_text: string }): Promise<AssessmentRepresentation> =>
    (await apiClient.post(`${P}/${eaId}/hr-respond`, body)).data,
  hrLock: async (eaId: string): Promise<EmployeeAssessment> =>
    (await apiClient.post(`${P}/${eaId}/hr-lock`)).data,
  reopen: async (eaId: string, reason: string): Promise<EmployeeAssessment> =>
    (await apiClient.post(`${P}/${eaId}/reopen`, { reason })).data,
  autoFetch: async (eaId: string): Promise<Record<string, unknown>> =>
    (await apiClient.post(`${P}/${eaId}/auto-fetch`)).data,
  recompute: async (eaId: string): Promise<EmployeeAssessment> =>
    (await apiClient.post(`${P}/${eaId}/recompute-score`)).data,
}
